/**
 * bookingPersistence — persistencia de progreso del wizard de agendamiento (UX_VISION P1-5).
 *
 * `sessionStorage`, NO `localStorage`: el blob lleva dato sensible (RUT, motivo de
 * consulta, datos de contacto) y no debe sobrevivir al cierre del navegador.
 *
 * Revisado por el agente `security` (engram: `sec/p1-5-wizard-persistence-review`):
 *  - solo se guarda el `therapistId`, nunca el objeto terapeuta (arrastra email/tarifa/campos internos)
 *  - `v` (schemaVersion) en cada blob → si no coincide, se descarta y se limpia
 *  - TTL corto: form 40 min (motivo + dirección), progreso 60 min — dato de salud en dispositivo posible compartido
 *  - el blob se trata como input NO confiable al leer: validación de forma + de cada string rehidratado
 *  - el banner de "reserva sin terminar" NUNCA previsualiza valores (solo tiempo relativo)
 *  - se limpia al confirmar la reserva, al elegir "empezar de nuevo" y por TTL
 *  - NO se persiste `emergenciaContacto`/`emergenciaTelefono` (PII de terceros) ni los checkboxes de consentimiento
 *  - sin cripto client-side (key en el bundle = teatro); las mitigaciones son TTL + minimizar + limpiar
 */

import type { SelectedService, SelectedTimeSlot, MetodoPagoDeclarado } from './BookingContext';

const SCHEMA_VERSION = 1;

const K = {
  progress: 'cc:booking:progress',
  form: 'cc:booking:patient-form',
  rut: 'cc:booking:rut',
} as const;

/** Clave histórica que vivía en `localStorage` y nunca se limpiaba (hallazgo `security`). */
const LEGACY_RUT_KEY = 'cc_patient_rut';

const TTL = {
  progress: 60 * 60_000, // 60 min
  form: 40 * 60_000,     // 40 min
  rut: 60 * 60_000,      // 60 min
} as const;

/** Pasos del wizard donde tiene sentido persistir progreso (rama paciente nuevo, post-RUT). */
const RESUMABLE_STEPS = ['service-therapist', 'calendar', 'patient-confirmation'] as const;
export type ResumableStep = typeof RESUMABLE_STEPS[number];

// ── helpers de storage ────────────────────────────────────────────────

function isBrowser(): boolean {
  return typeof window !== 'undefined' && !!window.sessionStorage;
}

function readJSON<T>(key: string): T | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as T) : null;
  } catch {
    return null;
  }
}

function writeJSON(key: string, value: unknown): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / modo privado — persistir es best-effort */
  }
}

function remove(key: string): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

/** `savedAt` válido = número finito, dentro del TTL y no en el futuro (blob manipulado). */
function fresh(savedAt: unknown, ttl: number): boolean {
  return (
    typeof savedAt === 'number' &&
    Number.isFinite(savedAt) &&
    savedAt <= Date.now() + 60_000 &&
    Date.now() - savedAt < ttl
  );
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

// ── blob del RUT ──────────────────────────────────────────────────────

export interface PersistedRut {
  rut: string;
  isExtranjero: boolean;
}

interface RutBlob extends PersistedRut {
  v: number;
  savedAt: number;
}

export function saveRut(rut: string, isExtranjero: boolean): void {
  const clean = str(rut).trim();
  if (!clean) return;
  writeJSON(K.rut, {
    v: SCHEMA_VERSION,
    savedAt: Date.now(),
    rut: clean,
    isExtranjero: !!isExtranjero,
  });
}

export function loadRut(): PersistedRut | null {
  const b = readJSON<RutBlob>(K.rut);
  if (!b || b.v !== SCHEMA_VERSION || !fresh(b.savedAt, TTL.rut) || !str(b.rut).trim()) {
    remove(K.rut);
    return null;
  }
  return { rut: b.rut.trim(), isExtranjero: !!b.isExtranjero };
}

// ── blob de progreso (paso + slice mínimo del contexto, sin PII) ───────

export interface PersistedProgress {
  step: ResumableStep;
  therapistId: string | null;
  service: SelectedService | null;
  timeSlot: SelectedTimeSlot | null;
  metodoPago: MetodoPagoDeclarado;
}

interface ProgressBlob extends PersistedProgress {
  v: number;
  savedAt: number;
}

function isResumableStep(s: unknown): s is ResumableStep {
  return typeof s === 'string' && (RESUMABLE_STEPS as readonly string[]).includes(s);
}

function sanitizeMetodo(m: unknown): MetodoPagoDeclarado {
  return m === 'tarjeta' ? 'tarjeta' : 'transferencia';
}

/** El servicio del blob es input no confiable: se revalida campo por campo. */
function sanitizeService(s: any): SelectedService | null {
  if (!s || typeof s !== 'object') return null;
  const therapyType = s.therapyType;
  const modality = s.modality;
  // FUNC-131: familia deshabilitada — un progreso guardado con familia se descarta.
  if (!['individual', 'pareja'].includes(therapyType)) return null;
  if (!['presencial', 'online', 'ambas', 'domicilio'].includes(modality)) return null;
  const duration = Number(s.duration);
  const price = Number(s.price);
  if (!Number.isFinite(duration) || duration <= 0) return null;
  if (!Number.isFinite(price) || price < 0) return null;
  return {
    id: str(s.id),
    therapyType,
    name: str(s.name),
    duration,
    price,
    modality,
  };
}

function sanitizeTimeSlot(t: any): SelectedTimeSlot | null {
  if (!t || typeof t !== 'object') return null;
  const date = str(t.date);
  const time = str(t.time);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!/^\d{1,2}:\d{2}$/.test(time)) return null;
  return { id: str(t.id), date, time };
}

export function saveProgress(p: {
  step: string;
  therapistId?: string | null;
  service: SelectedService | null;
  timeSlot: SelectedTimeSlot | null;
  metodoPago: MetodoPagoDeclarado;
}): void {
  // Fuera de la rama resumible (verification / existing-patient / success) → no persistir.
  if (!isResumableStep(p.step)) return;
  // Nada útil todavía → no ensuciar el storage ni disparar el banner.
  if (!p.therapistId && !p.service && !p.timeSlot) return;
  writeJSON(K.progress, {
    v: SCHEMA_VERSION,
    savedAt: Date.now(),
    step: p.step,
    therapistId: p.therapistId ?? null,
    service: p.service ?? null,
    timeSlot: p.timeSlot ?? null,
    metodoPago: sanitizeMetodo(p.metodoPago),
  });
}

export function loadProgress(): (PersistedProgress & { savedAt: number }) | null {
  const b = readJSON<ProgressBlob>(K.progress);
  if (!b || b.v !== SCHEMA_VERSION || !fresh(b.savedAt, TTL.progress) || !isResumableStep(b.step)) {
    remove(K.progress);
    return null;
  }
  return {
    savedAt: b.savedAt,
    step: b.step,
    therapistId: str(b.therapistId).trim() || null,
    service: sanitizeService(b.service),
    timeSlot: sanitizeTimeSlot(b.timeSlot),
    metodoPago: sanitizeMetodo(b.metodoPago),
  };
}

// ── blob del formulario del paso 3 ────────────────────────────────────

/**
 * Campos del formulario del paso 3 que SÍ se persisten. Lista explícita a propósito:
 * quedan FUERA los checkboxes de consentimiento (re-consentimiento fresco al volver),
 * `emergenciaContacto`/`emergenciaTelefono` (PII de terceros que no consintieron) y
 * `antecedentesMedicos`/`medicamentos`.
 */
export interface PersistedPatientForm {
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  genero: string;
  nombrePareja: string;
  direccion: string;
  motivoConsulta: string;
  preferenciaComunicacion: string;
}

const FORM_FIELDS: (keyof PersistedPatientForm)[] = [
  'nombre',
  'apellido',
  'email',
  'telefono',
  'genero',
  'nombrePareja',
  'direccion',
  'motivoConsulta',
  'preferenciaComunicacion',
];

/** Campos cuyo valor NO cuenta como "el paciente tipeó algo" (arrancan con default). */
const FORM_DEFAULTED: (keyof PersistedPatientForm)[] = ['genero', 'preferenciaComunicacion'];

interface FormBlob {
  v: number;
  savedAt: number;
  data: Partial<PersistedPatientForm>;
}

export function savePatientFormDraft(form: Record<string, unknown>): void {
  const data: Partial<PersistedPatientForm> = {};
  let hasContent = false;
  for (const f of FORM_FIELDS) {
    const val = form[f];
    if (typeof val === 'string' && val.trim()) {
      data[f] = val;
      if (!FORM_DEFAULTED.includes(f)) hasContent = true;
    }
  }
  if (!hasContent) {
    remove(K.form);
    return;
  }
  writeJSON(K.form, { v: SCHEMA_VERSION, savedAt: Date.now(), data });
}

export function loadPatientFormDraft(): Partial<PersistedPatientForm> | null {
  const b = readJSON<FormBlob>(K.form);
  if (!b || b.v !== SCHEMA_VERSION || !fresh(b.savedAt, TTL.form) || !b.data || typeof b.data !== 'object') {
    remove(K.form);
    return null;
  }
  const out: Partial<PersistedPatientForm> = {};
  for (const f of FORM_FIELDS) {
    const val = (b.data as Record<string, unknown>)[f];
    if (typeof val === 'string') out[f] = val;
  }
  return Object.keys(out).length ? out : null;
}

export function hasPatientFormDraft(): boolean {
  return loadPatientFormDraft() != null;
}

export function clearPatientFormDraft(): void {
  remove(K.form);
}

// ── limpieza y migración ──────────────────────────────────────────────

/** Borra TODO rastro del wizard. Se llama al confirmar la reserva y en "empezar de nuevo". */
export function clearBookingPersistence(): void {
  remove(K.progress);
  remove(K.form);
  remove(K.rut);
  if (isBrowser()) {
    try {
      localStorage.removeItem(LEGACY_RUT_KEY);
    } catch {
      /* noop */
    }
  }
}

/** Si el paciente re-verifica con un RUT distinto al guardado, el progreso/draft del
 *  anterior no le pertenece → se descarta (otra persona en la misma sesión). */
export function clearProgressForNewRut(newRut: string): void {
  const stored = loadRut();
  if (stored && stored.rut !== str(newRut).trim()) {
    remove(K.progress);
    remove(K.form);
  }
}

/** Migra el `cc_patient_rut` histórico de `localStorage` (nunca se limpiaba) a
 *  `sessionStorage` con TTL. Idempotente — seguro de llamar en cada mount. */
export function migrateLegacyRut(): void {
  if (!isBrowser()) return;
  try {
    const legacy = localStorage.getItem(LEGACY_RUT_KEY);
    if (!legacy) return;
    if (legacy.trim() && !sessionStorage.getItem(K.rut)) {
      saveRut(legacy.trim(), false);
    }
    localStorage.removeItem(LEGACY_RUT_KEY);
  } catch {
    /* noop */
  }
}

// ── tiempo relativo para el banner (sin exponer ningún dato) ───────────

const RTF =
  typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl
    ? new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
    : null;

export function relativeTimeFromNow(ts: number): string {
  const diffMin = Math.round((ts - Date.now()) / 60_000);
  if (RTF) {
    if (Math.abs(diffMin) < 60) return RTF.format(diffMin, 'minute');
    return RTF.format(Math.round(diffMin / 60), 'hour');
  }
  const m = Math.abs(diffMin);
  return m <= 1 ? 'recién' : `hace ${m} minutos`;
}
